import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import i18n from "@/i18n.ts";
import { WorkflowEditSidebar } from "@/pages/workflows/edit/components/WorkflowEditSidebar.tsx";
import {
  getApiWorkflowEventTypesSelectItems,
  getApiWorkflowScheduledEntityTypeSelectItems,
  getCustomScheduleDateColumns,
  hasAutomationConfigurationPage,
  useWorkflowEditPage,
  WorkflowEditPageProps,
  WorkflowEditPageStep,
} from "@/pages/workflows/edit/pages/workflow-edit-pages.tsx";
import { WorkflowTitleSlot } from "@/pages/workflows/edit/pages/WorkflowTitleSlot.tsx";
import { useEmployeeSearchColumns } from "@/queries/employees/use-employees.query.ts";
import {
  useWorkflowUpdateTrigger,
  useWorkflowUpdateTriggerConditions,
} from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  getAllowedConditionEntityTypesForMode,
  resolveTriggerConditionMode,
} from "@/pages/workflows/edit/actions/workflow-edit-block-condition-modal.tsx";
import {
  ApiWorkflowEventTypes,
  ApiWorkflowTriggerScheduleEntityField,
  ApiWorkflowTriggerScheduleEntityType,
  ApiWorkflowTriggerType,
  isCandidateWorkflowEventType,
  isCustomScheduleField,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowUpdateTrigger } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, FullScreenPage, Radio, Stack, Switch, Title } from "@skailer-com/uikit";
import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

const buildWorkflowEditTriggerSchema = () =>
  yup.object({
    trigger_type: yup
      .mixed<ApiWorkflowTriggerType>()
      .oneOf(Object.values(ApiWorkflowTriggerType))
      .required(i18n.t("errors:field_required")),
    event_type: yup
      .mixed<ApiWorkflowEventTypes>()
      .oneOf(Object.values(ApiWorkflowEventTypes))
      .nullable()
      .defined(),
    schedule_entity_type: yup
      .mixed<ApiWorkflowTriggerScheduleEntityType>()
      .oneOf(Object.values(ApiWorkflowTriggerScheduleEntityType))
      .nullable()
      .defined(),
    schedule_entity_field: yup
      .mixed<ApiWorkflowTriggerScheduleEntityField>()
      .oneOf(Object.values(ApiWorkflowTriggerScheduleEntityField))
      .nullable()
      .defined(),
  });

export const WorkflowEditAutomationPage = ({
  workflow,
  workflowVersion,
}: WorkflowEditPageProps) => {
  const { t } = useTranslation();
  const { isFeatureEnabled } = usePermissions();
  const { confirmExit, navigateToStep } = useWorkflowEditPage();
  const isAtsEnabled = isFeatureEnabled(ApiFeatureType.ats);

  useTitle(t("workflow:choose_automation_trigger"), t("workflow:workflows"));

  const workflowEditTriggerSchema = buildWorkflowEditTriggerSchema();

  const {
    control,
    setValue,
    handleSubmit,
    formState: { isDirty },
  } = useForm<ApiWorkflowUpdateTrigger>({
    mode: "onSubmit",
    resolver: yupResolver(workflowEditTriggerSchema),
    values: {
      trigger_type: workflow.trigger_type,
      event_type: workflowVersion.trigger_event?.event_type || null,
      schedule_entity_field: workflowVersion.trigger_schedule_rule?.entity_field || null,
      schedule_entity_type: workflowVersion.trigger_schedule_rule?.entity_type || null,
    },
  });
  const workflowUpdateTrigger = useWorkflowUpdateTrigger(control);
  const workflowUpdateTriggerConditions = useWorkflowUpdateTriggerConditions(control);

  const [schedule_entity_type, schedule_entity_field, triggerType, eventType] = useWatch({
    control,
    name: ["schedule_entity_type", "schedule_entity_field", "trigger_type", "event_type"],
  });

  const handleTriggerTypeChange = (triggerType: ApiWorkflowTriggerType) => {
    setValue("trigger_type", triggerType, { shouldDirty: true });

    if (triggerType !== ApiWorkflowTriggerType.event) {
      setValue("event_type", null, { shouldDirty: true });
    }

    if (triggerType !== ApiWorkflowTriggerType.scheduled) {
      setValue("schedule_entity_type", null, { shouldDirty: true });
      setValue("schedule_entity_field", null, { shouldDirty: true });
    }
  };

  const handleSave = handleSubmit(async (data) => {
    const payload: ApiWorkflowUpdateTrigger = isUseAutomation
      ? data
      : {
          trigger_type: ApiWorkflowTriggerType.manual,
          event_type: null,
          schedule_entity_type: null,
          schedule_entity_field: null,
        };

    const nextTriggerIsTimeOff =
      (payload.trigger_type === ApiWorkflowTriggerType.scheduled &&
        payload.schedule_entity_type === ApiWorkflowTriggerScheduleEntityType.time_off) ||
      payload.event_type === ApiWorkflowEventTypes.time_off_request_approved;
    const allowedEntityTypes = getAllowedConditionEntityTypesForMode(
      resolveTriggerConditionMode(
        isCandidateWorkflowEventType(payload.event_type),
        nextTriggerIsTimeOff
      )
    );
    const nextConditionsPayload = workflowVersion.trigger_conditions
      .filter((condition) => allowedEntityTypes.includes(condition.entity_type))
      .map((condition) => ({
        entity_type: condition.entity_type,
        field_name: condition.field_name,
        operator: condition.operator,
        field_value: condition.field_value,
        condition_group: condition.condition_group,
      }));
    const shouldUpdateConditions =
      nextConditionsPayload.length !== workflowVersion.trigger_conditions.length;

    const nextPageType = hasAutomationConfigurationPage(
      payload.trigger_type,
      payload.schedule_entity_type ?? undefined
    )
      ? WorkflowEditPageStep.automation_setup
      : WorkflowEditPageStep.flow;

    if (!isDirty) {
      navigateToStep(workflow.id, nextPageType);
      return;
    }

    try {
      await workflowUpdateTrigger.mutateAsync({
        workflowId: workflow.id,
        workflowVersionId: workflow.current_version_id,
        payload,
      });

      if (shouldUpdateConditions) {
        await workflowUpdateTriggerConditions.mutateAsync({
          workflowId: workflow.id,
          workflowVersionId: workflow.current_version_id,
          payload: nextConditionsPayload,
        });
      }

      analyticsService.trackEvent(analyticEvents.workflows.edited, {
        [analyticProperties.id]: workflow.id,
        [analyticProperties.tab]: WorkflowEditPageStep.automation,
      });

      navigateToStep(workflow.id, nextPageType);
    } catch {
      //  handle error
    }
  });

  const [isUseAutomation, seIisUseAutomation] = useState<boolean>(
    workflow.trigger_type != ApiWorkflowTriggerType.manual
  );

  const { data: employeeColumns } = useEmployeeSearchColumns(isUseAutomation);
  const scheduledEntityTypeItems = getApiWorkflowScheduledEntityTypeSelectItems().filter(
    (item) =>
      !isCustomScheduleField(item.field) ||
      getCustomScheduleDateColumns(item.field, employeeColumns).length > 0
  );

  return (
    <FullScreenPage
      isCenterVertical={false}
      titleSlot={
        <WorkflowTitleSlot
          step={WorkflowEditPageStep.automation}
          workflow={workflow}
          workflowVersion={workflowVersion}
        />
      }
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit()}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <Stack direction={"horizontal"} gap={"sm"}>
          <Button
            onClick={() => {
              navigateToStep(workflow.id, WorkflowEditPageStep.visibility);
            }}
            variant="secondary"
            size={"lg"}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={handleSave}>
            {t("common:continue")}
          </Button>
        </Stack>
      }
      sidebarSlot={
        <WorkflowEditSidebar
          step={WorkflowEditPageStep.automation}
          workflow={workflow}
          workflowVersion={workflowVersion}
        />
      }
    >
      <form onSubmit={handleSave}>
        <Stack gap={"sm"} justify={"center"}>
          <Stack className={"card-panel"} gap={"lg"}>
            <Title
              size={"lg"}
              header={t("workflow:choose_automation_trigger")}
              caption={t("workflow:choose_automation_trigger_description")}
            />
            <Switch
              size={"sm"}
              checked={!isUseAutomation}
              onChange={(value) => {
                seIisUseAutomation(!value);
                handleTriggerTypeChange(ApiWorkflowTriggerType.manual);
              }}
              title={t("workflow:do_not_use_automation_title")}
              description={t("workflow:do_not_use_automation_caption")}
            />

            {isUseAutomation && (
              <Box variant={"border"}>
                <Title header={t("workflow:events")} size={"xs"} />
                <Controller
                  control={control}
                  name="event_type"
                  render={({ field: { value, onChange } }) => (
                    <Stack>
                      {getApiWorkflowEventTypesSelectItems(isAtsEnabled).map((item) => {
                        const [effectiveEventType, createdEventType] = item.event_types;
                        const isSelected = eventType ? item.event_types.includes(eventType) : false;

                        return (
                          <Stack key={item.id}>
                            <Radio
                              title={item.title}
                              caption={item.caption}
                              checked={isSelected}
                              onChange={() => {
                                onChange(effectiveEventType);
                                handleTriggerTypeChange(ApiWorkflowTriggerType.event);
                              }}
                            />
                            {isSelected && createdEventType && (
                              <Stack className="pl-7 pb-2">
                                <Radio
                                  title={t("workflow:event_trigger_moment_created")}
                                  checked={value === createdEventType}
                                  onChange={() => onChange(createdEventType)}
                                />
                                <Radio
                                  title={t("workflow:event_trigger_moment_effective")}
                                  checked={value === effectiveEventType}
                                  onChange={() => onChange(effectiveEventType)}
                                />
                              </Stack>
                            )}
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}
                />
              </Box>
            )}

            {isUseAutomation && (
              <Box variant={"border"}>
                <Title header={t("workflow:time_sensitive")} size={"xs"} />

                <Stack>
                  {scheduledEntityTypeItems.map((item) => (
                    <Radio
                      key={`${item.entity}_${item.field}`}
                      title={item.title}
                      caption={item.caption}
                      checked={
                        item.entity == schedule_entity_type && item.field == schedule_entity_field
                      }
                      onChange={() => {
                        handleTriggerTypeChange(ApiWorkflowTriggerType.scheduled);
                        setValue("schedule_entity_type", item.entity, {
                          shouldDirty: true,
                        });
                        setValue("schedule_entity_field", item.field, {
                          shouldDirty: true,
                        });
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {isUseAutomation && (
              <Box variant="border">
                <Title header={t("workflow:trigger_recurring_title")} size="xs" />
                <Radio
                  title={t("workflow:trigger_recurring_option")}
                  caption={t("workflow:trigger_recurring_option_description")}
                  checked={triggerType === ApiWorkflowTriggerType.recurring}
                  onChange={() => handleTriggerTypeChange(ApiWorkflowTriggerType.recurring)}
                />
              </Box>
            )}
          </Stack>
          <Button type={"submit"} size={"lg"} isLoading={workflowUpdateTrigger.isPending}>
            {t("common:continue")}
          </Button>
        </Stack>
      </form>
    </FullScreenPage>
  );
};
